import { type AddOwnOptionProps, OwnOptionInUseError } from "@patient-watch/forms";
import type { InputT } from "@patient-watch/types";
import { useMemo } from "react";

import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { deleteOwnOption, insertOwnOption, updateOwnOption } from "./api/insertOwnOption";
import { ownOptionsOf } from "./ownOptions";
import { useOwnOptionsStore, useViewersOwnOptions } from "./useOwnOptionsStore";

/**
 * What a single- or multiple-choice question needs for "add your own option", or undefined when the question
 * doesn't allow it. Signed in, own options are saved straight away (add_own_option) and come back next time.
 * Anonymous (a public link) or in a preview, they stay in this browser: a public link saves them on submit, a
 * preview never does.
 */
export function useAddOwnOption(
    input: InputT,
    viewer: { userId: string | null | undefined; entryId: string | null | undefined },
    options: { saveToDatabase: boolean }
): Omit<AddOwnOptionProps, "inputId" | "onAdded" | "multiple"> | undefined {
    const copy = useFormPageCopy();
    const { userId, entryId } = viewer;
    const known = useViewersOwnOptions({ userId, entryId });
    const upsertOwnOption = useOwnOptionsStore((state) => state.upsertOwnOption);
    const removeOwnOption = useOwnOptionsStore((state) => state.removeOwnOption);
    const signedIn = Boolean(userId) && options.saveToDatabase;

    return useMemo(() => {
        if (!input.allow_add_option || !entryId) return undefined;
        const remember = (option: { option_id: string; label: string; deleted?: boolean }, pending: boolean) =>
            upsertOwnOption({
                ...option,
                input_id: input.input_id,
                entry_id: entryId,
                user_id: userId || null,
                pending,
            });
        return {
            label: input.add_option_label?.trim() || copy.ownOptionOther,
            ownOptions: ownOptionsOf(input, { userId, entryId }, known).map((option) => ({
                value: option.option_id,
                label: option.label,
            })),
            onAdd: async (label) => {
                if (signedIn) {
                    const saved = await insertOwnOption(input.input_id, label);
                    remember(saved, false);
                    return saved.option_id;
                }
                // Saved with the entry on submit; until then its id is made here, and is its value.
                const option_id = crypto.randomUUID();
                remember({ option_id, label }, true);
                return option_id;
            },
            onDelete: async (value) => {
                const stored = known.find((option) => option.option_id === value);
                if (signedIn && !stored?.pending) {
                    try {
                        await deleteOwnOption(value, entryId);
                    } catch (error) {
                        if (isOwnOptionInUse(error)) throw new OwnOptionInUseError();
                        throw error;
                    }
                    // The page's rows may still hold it until they reload, so remember it is gone.
                    remember({ option_id: value, label: stored?.label ?? "", deleted: true }, false);
                    return;
                }
                // Never saved: forget it, and the submit won't send it.
                removeOwnOption(value);
            },
            onRename: async (value, label) => {
                const stored = known.find((option) => option.option_id === value);
                if (signedIn && !stored?.pending) {
                    remember(await updateOwnOption(value, label), false);
                    return;
                }
                remember({ option_id: value, label }, stored?.pending ?? true);
            },
            copy: {
                placeholder: copy.ownOptionPlaceholder,
                add: copy.ownOptionAdd,
                save: copy.ownOptionSave,
                cancel: copy.ownOptionCancel,
                edit: copy.ownOptionEdit,
                delete: copy.ownOptionDelete,
                inUse: copy.ownOptionInUse,
                empty: copy.ownOptionEmpty,
                tooLong: copy.ownOptionTooLong,
                limit: copy.ownOptionLimit,
                failed: copy.ownOptionFailed,
            },
        };
    }, [input, entryId, userId, signedIn, known, upsertOwnOption, removeOwnOption, copy]);
}

/** delete_own_option refuses an option another entry's answer uses, with the hint own_option_in_use. */
function isOwnOptionInUse(error: unknown): boolean {
    return typeof error === "object" && error !== null && (error as { hint?: unknown }).hint === "own_option_in_use";
}
